import { useCallback, useEffect, useState } from 'react'
import api from '../utils/api'
import { Button } from '@/components/Button'
import { SetupRequiredState } from '@/components/SetupRequiredState'
import { useToast } from '@/components/Toast'
import { parseApiError, type ParsedApiError } from '@/utils/apiErrors'

export default function BeaconVpc() {
  const { toast } = useToast()
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [setupError, setSetupError] = useState<ParsedApiError | null>(null)

  const load = useCallback(async (refresh = false) => {
    setLoading(true)
    try {
      const res = await api.get('/api/v1/beacon/vpc', { params: { refresh } })
      setData(res.data)
      setSetupError(null)
    } catch (err: unknown) {
      const parsed = parseApiError(err)
      if (parsed.isSetupIncomplete || parsed.status === 404 || parsed.status === 409 || parsed.status === 422) {
        setSetupError(parsed)
        setData(null)
      } else {
        toast.error('VPC load failed', parsed.message)
      }
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load(false) }, [load])

  const snap = data?.snapshot || {}

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-slate-900">VPC & network</h2>
          <p className="text-sm text-slate-600">Beacon’s understanding of your VPCs, subnets, SGs, and routes.</p>
        </div>
        <Button onClick={() => load(true)} disabled={!!setupError}>Refresh from AWS</Button>
      </div>

      {loading && <p className="text-sm text-slate-400">Loading…</p>}
      {!loading && setupError && (
        <SetupRequiredState
          message={setupError.message}
          cta={setupError.cta}
        />
      )}
      {!loading && !setupError && data && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="VPCs" value={data.vpc_count} />
            <Stat label="Subnets" value={(snap.subnets || []).length} />
            <Stat label="Open SGs" value={(snap.open_security_groups || []).length} />
          </div>

          {(snap.advice_hints || []).length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">Advice</p>
              <ul className="mt-1 list-disc pl-5">
                {snap.advice_hints.map((h: string) => <li key={h}>{h}</li>)}
              </ul>
            </div>
          )}

          <Section title="VPCs">
            {(snap.vpcs || []).map((v: any) => (
              <div key={v.vpc_id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                <span className="font-medium">{v.name || v.vpc_id}</span>
                <span className="ml-2 text-slate-500">{v.cidr}</span>
                <span className="ml-2 text-xs text-slate-400">{v.subnet_count} subnets · IGW {v.has_igw ? 'yes' : 'no'}</span>
              </div>
            ))}
          </Section>

          <Section title="Subnets">
            {(snap.subnets || []).map((s: any) => (
              <div key={s.subnet_id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                <span className={`mr-2 rounded-full px-2 py-0.5 text-xs ${s.tier === 'public' ? 'bg-sky-100 text-sky-800' : 'bg-slate-100 text-slate-700'}`}>{s.tier}</span>
                {s.subnet_id} · {s.cidr} · {s.az}
              </div>
            ))}
          </Section>

          <Section title="Security groups with 0.0.0.0/0">
            {(snap.open_security_groups || []).length === 0 && (
              <p className="text-sm text-slate-400">None detected.</p>
            )}
            {(snap.open_security_groups || []).map((sg: any) => (
              <div key={sg.group_id} className="rounded-lg border border-red-100 bg-red-50/40 px-3 py-2 text-sm">
                {sg.group_name} ({sg.group_id}) — {JSON.stringify(sg.open_world_ingress)}
              </div>
            ))}
          </Section>
        </>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="font-display text-3xl text-slate-900">{value}</p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  )
}
